import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LicensureState } from 'src/app/compliance/models/posted-documents-models';
import * as stateLicActions from 'src/app/compliance/store/actions/state-licensure.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { downloadBlob } from 'src/app/shared/utilities/download-blob';
import { StateLicensureNSwagMigrationService } from '../../api/migration/state-licensure-nswag-migration.service';
import { StateLicensureAttachment } from '../../state-licensure/grid/models/state-licensure-details.model';

@Injectable()
export class StateLicensureEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _store: Store<LicensureState>,
        private readonly _stateLicensureService: StateLicensureNSwagMigrationService
    ) {}

    loadStateLicensureDetails$ = createEffect(() =>
        this._actions$.pipe(
            ofType(stateLicActions.getStateDetails, stateLicActions.updateDetailLicSuccess),
            switchMap((actions) =>
                this._stateLicensureService.getDetails(actions.stateId).pipe(
                    map((res) => {
                        const resDetails = res[0];
                        return stateLicActions.getStateDetailsSuccess({ details: resDetails });
                    }),
                    catchError((error) => of(stateLicActions.getStateDetailsFailure({ error })))
                )
            )
        )
    );

    loadStateLicensureAttachments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                stateLicActions.getStateDetailsAttachments,
                stateLicActions.getStateDetails,
                stateLicActions.uploadStateDetailsFileSuccess,
                stateLicActions.deleteStateDetailsFileAttachmentSuccess
            ),
            switchMap((actions) => {
                return this._stateLicensureService.getAttachments(actions.stateId).pipe(
                    map((files: StateLicensureAttachment[]) => stateLicActions.getStateDetailsAttachmentsSuccess({ attachments: files })),
                    catchError((error) => of(stateLicActions.getStateDetailsAttachmentsFailure({ error })))
                );
            })
        )
    );

    loadStateLicensureProjectTypes = createEffect(() =>
        this._actions$.pipe(
            ofType(stateLicActions.getProjectTypes),
            switchMap((actions) =>
                this._stateLicensureService.getProjectTypes().pipe(
                    map((projectTypes) => stateLicActions.getProjectTypesSuccess({ projectTypes })),
                    catchError((error) => of(stateLicActions.getProjectTypesFailure({ error })))
                )
            )
        )
    );

    downloadAttachmentFile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(stateLicActions.downloadStateDetailsFileAttachment),
            switchMap((action) => {
                return this._stateLicensureService.downloadAttachments(action.fileId, action.stateId).pipe(
                    map((blob) => {
                        downloadBlob(blob, action.fileName);
                        return stateLicActions.downloadStateDetailsFileAttachmentSuccess({
                            blob,
                            fileName: action.fileName
                        });
                    }),
                    catchError((error) => of(stateLicActions.downloadStateDetailsFileAttachmentFailure({ error })))
                );
            })
        )
    );

    deleteAttachmentFile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(stateLicActions.deleteStateDetailsFileAttachment),
            switchMap((actions) =>
                this._stateLicensureService.deleteAttachment(actions.fileId, actions.stateId).pipe(
                    map((file) =>
                        stateLicActions.deleteStateDetailsFileAttachmentSuccess({
                            stateId: actions.stateId,
                            file
                        })
                    ),
                    catchError((error) => of(stateLicActions.deleteStateDetailsFileAttachmentFailure({ error })))
                )
            )
        )
    );

    uploadStateLicensureFile = createEffect(() =>
        this._actions$.pipe(
            ofType(stateLicActions.uploadStateDetailsFile),
            switchMap((actions) =>
                this._stateLicensureService.uploadFile(actions.stateId, actions.file).pipe(
                    map((file) => stateLicActions.uploadStateDetailsFileSuccess({ stateId: actions.stateId, file })),
                    catchError((error) => of(stateLicActions.uploadStateDetailsFileFailure({ error })))
                )
            )
        )
    );

    loadStateLicensureAdmins = createEffect(() =>
        this._actions$.pipe(
            ofType(stateLicActions.getAdmins),
            switchMap((actions) =>
                this._stateLicensureService.getAdmins().pipe(
                    map((admins) => stateLicActions.getAdminsSuccess({ admins })),
                    catchError((error) => of(stateLicActions.getAdminsFailure({ error })))
                )
            )
        )
    );

    updateStateLicensure = createEffect(() =>
        this._actions$.pipe(
            ofType(stateLicActions.updateDetailLic),
            switchMap((actions) =>
                this._stateLicensureService.updateLic(actions.detail, actions.id, actions.stateId).pipe(
                    map(() => stateLicActions.updateDetailLicSuccess({ stateId: actions.stateId })),
                    catchError((error) => of(stateLicActions.updateDetailLicFailure({ error })))
                )
            )
        )
    );

    updatedSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(stateLicActions.updateDetailLicSuccess),
                map(() => this._store.dispatch(showToasterMessage({ message: 'Licensure updated success' })))
            ),
        { dispatch: false }
    );

    updateFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(stateLicActions.updateDetailLicFailure),
                map(() => this._store.dispatch(showToasterMessage({ message: 'Licensure updated failure' })))
            ),
        { dispatch: false }
    );

    closeEdit$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(stateLicActions.updateDetailLicSuccess, stateLicActions.updateDetailLicFailure),
                map(() => this._store.dispatch(stateLicActions.toggleEditMode({ isEditMode: false })))
            ),
        { dispatch: false }
    );
}
